import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE } from '../constants/theme';

/**
 * StatCategoryManager Component
 *
 * Allows coaches and admins to manage organization-level stat categories
 * for performance tracking (e.g., Exit Velocity, 60-Yard Dash, etc.)
 *
 * @param {Object} props
 * @param {Object} props.profile - Coach/admin profile with organization_id
 */
const StatCategoryManager = ({ profile }) => {
  const { isDesktop } = useResponsive();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    sort_direction: 'desc',
    description: '',
  });

  useEffect(() => {
    if (profile?.organization_id) {
      loadCategories();
    }
  }, [profile?.organization_id]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('stat_categories')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('display_order')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error loading stat categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      unit: '',
      sort_direction: 'desc',
      description: '',
    });
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      unit: category.unit,
      sort_direction: category.sort_direction,
      description: category.description || '',
    });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      unit: '',
      sort_direction: 'desc',
      description: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('stat_categories')
          .update({
            name: formData.name,
            unit: formData.unit,
            sort_direction: formData.sort_direction,
            description: formData.description || null,
          })
          .eq('id', editingCategory.id);

        if (error) throw error;
        setSuccessMessage('Stat category updated successfully');
      } else {
        // Create new category
        const { error } = await supabase.from('stat_categories').insert([
          {
            organization_id: profile.organization_id,
            name: formData.name,
            unit: formData.unit,
            sort_direction: formData.sort_direction,
            description: formData.description || null,
            created_by: profile.id,
            display_order: categories.length,
          },
        ]);

        if (error) throw error;
        setSuccessMessage('Stat category created successfully');
      }

      handleCancelForm();
      loadCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving stat category:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (category) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${category.name}"?\n\nThis will not delete existing player stats, but they will no longer be linked to this category.`
      )
    ) {
      return;
    }

    try {
      setError('');
      const { error } = await supabase.from('stat_categories').delete().eq('id', category.id);

      if (error) throw error;

      setSuccessMessage('Stat category deleted successfully');
      loadCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting stat category:', err);
      setError(err.message);
    }
  };

  const styles = {
    container: {
      padding: isDesktop ? SPACING.xl : SPACING.md,
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '24px' : '16px',
      marginBottom: SPACING.lg,
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: SPACING.lg,
      flexWrap: 'wrap',
      gap: SPACING.md,
    },
    title: {
      fontSize: isDesktop ? FONT_SIZE.xl : FONT_SIZE.lg,
      fontWeight: '600',
      color: COLORS.gray[900],
      margin: 0,
    },
    button: {
      padding: isDesktop ? '8px 16px' : '10px 16px',
      fontSize: FONT_SIZE.base,
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: COLORS.primary,
      color: 'white',
    },
    secondaryButton: {
      backgroundColor: COLORS.gray[300],
      color: COLORS.gray[700],
    },
    dangerButton: {
      backgroundColor: COLORS.danger,
      color: 'white',
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isDesktop ? '600px' : '100%',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: COLORS.gray[600],
      fontSize: FONT_SIZE.sm,
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: FONT_SIZE.sm,
    },
    formGroup: {
      marginBottom: SPACING.lg,
    },
    label: {
      display: 'block',
      marginBottom: SPACING.sm,
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      color: COLORS.gray[700],
    },
    input: {
      width: '100%',
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      border: `1px solid ${COLORS.gray[300]}`,
      borderRadius: '4px',
      boxSizing: 'border-box',
      minHeight: '44px',
    },
    textarea: {
      width: '100%',
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      border: `1px solid ${COLORS.gray[300]}`,
      borderRadius: '4px',
      boxSizing: 'border-box',
      minHeight: '100px',
      fontFamily: 'inherit',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      border: `1px solid ${COLORS.gray[300]}`,
      borderRadius: '4px',
      backgroundColor: 'white',
      minHeight: '44px',
    },
    buttonGroup: {
      display: 'flex',
      gap: SPACING.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: COLORS.gray[500],
    },
    alert: {
      padding: SPACING.md,
      borderRadius: '4px',
      marginBottom: SPACING.md,
      fontSize: FONT_SIZE.sm,
    },
    error: {
      backgroundColor: COLORS.alert.error.bg,
      color: COLORS.alert.error.text,
      border: `1px solid ${COLORS.alert.error.border}`,
    },
    success: {
      backgroundColor: COLORS.alert.success.bg,
      color: COLORS.alert.success.text,
      border: `1px solid ${COLORS.alert.success.border}`,
    },
    badge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    badgeDesc: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
    },
    badgeAsc: {
      backgroundColor: '#fff3e0',
      color: '#f57c00',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p>Loading stat categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {error && <div style={{ ...styles.alert, ...styles.error }}>{error}</div>}

      {successMessage && <div style={{ ...styles.alert, ...styles.success }}>{successMessage}</div>}

      {showForm ? (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.title}>
              {editingCategory ? 'Edit Stat Category' : 'Create Stat Category'}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name * (e.g., "Exit Velocity", "60-Yard Dash")</label>
              <input
                type="text"
                style={styles.input}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Stat name"
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Unit * (e.g., "mph", "seconds", "average")</label>
              <input
                type="text"
                style={styles.input}
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="Unit of measurement"
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Sort Direction * (How are players ranked?)</label>
              <select
                style={styles.select}
                value={formData.sort_direction}
                onChange={(e) => setFormData({ ...formData, sort_direction: e.target.value })}
                required
              >
                <option value="desc">Higher is Better (e.g., Exit Velocity)</option>
                <option value="asc">Lower is Better (e.g., 60-Yard Dash Time)</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description (Optional)</label>
              <textarea
                style={styles.textarea}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this metric and how it's measured"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={handleCancelForm}
              >
                Cancel
              </button>
              <button type="submit" style={{ ...styles.button, ...styles.primaryButton }}>
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.title}>Stat Categories</h2>
            <button style={{ ...styles.button, ...styles.primaryButton }} onClick={handleCreateNew}>
              + Create Category
            </button>
          </div>

          {categories.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No stat categories yet.</p>
              <p>Create categories to start tracking player performance metrics.</p>
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Unit</th>
                    <th style={styles.th}>Ranking</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td style={styles.td}>
                        <strong>{category.name}</strong>
                      </td>
                      <td style={styles.td}>{category.unit}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            ...(category.sort_direction === 'desc'
                              ? styles.badgeDesc
                              : styles.badgeAsc),
                          }}
                        >
                          {category.sort_direction === 'desc' ? 'Higher Better' : 'Lower Better'}
                        </span>
                      </td>
                      <td style={styles.td}>{category.description || '-'}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: COLORS.primary,
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleEdit(category)}
                          >
                            Edit
                          </button>
                          <button
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: COLORS.danger,
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleDelete(category)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatCategoryManager;
